import { useCallback, useEffect, useMemo, useState } from 'react';
import { getText } from 'i18n/intl';
import { v4 as uuid } from 'uuid';
import type {
  LocationFieldSetting,
  LocationTemplate,
} from '@timeedit/calendar-integration-api-contract';
import { Icon } from '@timeedit/ui-components';
import type { TableColumnsType } from '@timeedit/ui-components/antd';
import { Button, Space, Table } from '@timeedit/ui-components/antd';
import DndWrapper, { DragHandle, Row, arrayMove } from './DndWrapper';
import EditTemplateModal from './EditTemplateModal';
import FieldSettingTag from './FieldSettingTag';
import {
  type LocationTemplateRow,
  locationRowsToTemplates,
  locationTemplatesToRows,
} from './fieldSettingUtils';
import { useTemplateFieldOptions } from './useTemplateFieldOptions';
import './SubjectPicker.scss';

export const intlPrefix = 'integration.calendar.sync_settings';

export type LocationPickerProps = {
  values: LocationTemplate[];
  onChange: (items: LocationTemplate[]) => void;
  addBtnLabel: string;
  removeBtnLabel: string;
};

type TableFooterProps = {
  onAdd: () => void;
  onRemove: () => void;
  addLabel: string;
  removeLabel: string;
  removeDisabled: boolean;
};

const TableFooter = ({
  onAdd,
  onRemove,
  addLabel,
  removeLabel,
  removeDisabled,
}: TableFooterProps) => (
  <Space direction="horizontal">
    <Button size="small" onClick={onAdd} type="primary">
      {addLabel}
    </Button>
    <Button size="small" onClick={onRemove} disabled={removeDisabled} danger>
      {removeLabel}
    </Button>
  </Space>
);

const createColumns = (
  onTemplateEdit: (index: number) => void,
  onRemoveTemplateIds: (ids: string[]) => void
): TableColumnsType<LocationTemplateRow> => [
  {
    key: 'dragAndDrop',
    width: '32px',
    render: () => <DragHandle />,
  },
  Table.SELECTION_COLUMN,
  {
    key: 'order',
    title: '#',
    render: (_value, _record, index) => index + 1,
    width: '32px',
  },
  {
    key: 'value',
    title: getText(`${intlPrefix}.form.sync_mode.te_out.template_column.value`),
    render: (_, record) => (
      <Space>
        {record.fields.map((fieldSetting) => (
          <FieldSettingTag key={fieldSetting.id} fieldSetting={fieldSetting} />
        ))}
      </Space>
    ),
  },
  {
    key: 'actions',
    title: '',
    width: 80,
    render: (_value, record, index) => (
      <Space direction="horizontal">
        <Button
          onClick={() => onTemplateEdit(index)}
          size="small"
          icon={<Icon id="EditOutlined" alt="Edit" />}
        />
        <Button
          onClick={() => onRemoveTemplateIds([record.id])}
          size="small"
          icon={<Icon id="DeleteOutlined" alt="Delete" />}
        />
      </Space>
    ),
  },
];

const LocationPicker = ({
  values,
  onChange,
  addBtnLabel,
  removeBtnLabel,
}: LocationPickerProps) => {
  const [localTemplates, setLocalTemplates] = useState<LocationTemplateRow[]>(
    () => locationTemplatesToRows(values)
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { getLocationTokenOptions } = useTemplateFieldOptions();

  // Sync local state when values prop changes (e.g., switching sync settings)
  useEffect(() => {
    setLocalTemplates(locationTemplatesToRows(values));
    setSelectedIds([]);
  }, [values]);

  /**
   * Edit Template Modal state
   * null = closed
   * -1 = adding new
   * >= 0 = editing at index
   */
  const [editingTemplateIndex, setEditingTemplateIndex] = useState<
    number | null
  >(null);

  const isEditTemplateModalOpen = useMemo(
    () => editingTemplateIndex !== null,
    [editingTemplateIndex]
  );
  const editingTemplateFields = useMemo(() => {
    if (editingTemplateIndex === null || editingTemplateIndex === -1) return [];
    return localTemplates[editingTemplateIndex]?.fields ?? [];
  }, [editingTemplateIndex, localTemplates]);

  const handleAddTemplate = useCallback(
    (fields: LocationFieldSetting[]) => {
      const newTemplate: LocationTemplateRow = {
        id: `template-${uuid()}`,
        fields,
      };
      setLocalTemplates((prev) => {
        const newTemplates = [...prev, newTemplate];
        onChange(locationRowsToTemplates(newTemplates));
        return newTemplates;
      });
    },
    [onChange]
  );

  const handleUpdateTemplate = useCallback(
    (fields: LocationFieldSetting[], index: number) => {
      setLocalTemplates((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], fields };
        onChange(locationRowsToTemplates(updated));
        return updated;
      });
    },
    [onChange]
  );

  const handleEditTemplateOk = useCallback(
    (fields: LocationFieldSetting[]) => {
      if (editingTemplateIndex === -1) {
        handleAddTemplate(fields);
      } else if (editingTemplateIndex !== null && editingTemplateIndex >= 0) {
        handleUpdateTemplate(fields, editingTemplateIndex);
      }
      setEditingTemplateIndex(null);
    },
    [editingTemplateIndex, handleAddTemplate, handleUpdateTemplate]
  );

  const handleEditTemplateCancel = useCallback(() => {
    setEditingTemplateIndex(null);
  }, []);

  const handleEditTemplateRemove = useCallback(() => {
    if (editingTemplateIndex !== null && editingTemplateIndex >= 0) {
      setLocalTemplates((prev) => {
        const templateToRemove = prev[editingTemplateIndex];
        if (!templateToRemove) return prev;
        const newTemplates = prev.filter((t) => t.id !== templateToRemove.id);
        onChange(locationRowsToTemplates(newTemplates));
        return newTemplates;
      });
    }
    setEditingTemplateIndex(null);
  }, [editingTemplateIndex, onChange]);

  const onTemplateAdd = useCallback(() => {
    setEditingTemplateIndex(-1);
  }, []);

  const onTemplateEdit = useCallback((index: number) => {
    setEditingTemplateIndex(index);
  }, []);

  const onRemoveTemplateIds = useCallback(
    (ids: string[]) => {
      setLocalTemplates((prev) => {
        const newTemplates = prev.filter((t) => !ids.includes(t.id));
        onChange(locationRowsToTemplates(newTemplates));
        return newTemplates;
      });
      setSelectedIds([]);
    },
    [onChange]
  );

  const onDragEnd = useCallback(
    (sourceIndex: number, targetIndex: number) => {
      setLocalTemplates((prevState) => {
        const newList = arrayMove(prevState, sourceIndex, targetIndex);
        onChange(locationRowsToTemplates(newList));
        return newList;
      });
    },
    [onChange]
  );

  const columns = useMemo(
    () => createColumns(onTemplateEdit, onRemoveTemplateIds),
    [onTemplateEdit, onRemoveTemplateIds]
  );

  return (
    <div className="subject-picker--wrapper">
      <DndWrapper onDragEnd={onDragEnd} items={localTemplates.map((t) => t.id)}>
        <Table<LocationTemplateRow>
          rowKey={'id'}
          size="small"
          columns={columns}
          dataSource={localTemplates}
          rowSelection={{
            onChange: (selectedRowKeys) => {
              setSelectedIds(selectedRowKeys as string[]);
            },
          }}
          footer={() => (
            <TableFooter
              onAdd={onTemplateAdd}
              onRemove={() => onRemoveTemplateIds(selectedIds)}
              addLabel={addBtnLabel}
              removeLabel={removeBtnLabel}
              removeDisabled={selectedIds.length === 0}
            />
          )}
          scroll={{ x: 382, y: 300 }}
          components={{ body: { row: Row } }}
          pagination={false}
        />
      </DndWrapper>
      <EditTemplateModal
        open={isEditTemplateModalOpen}
        initialFields={editingTemplateFields}
        onOk={handleEditTemplateOk}
        onCancel={handleEditTemplateCancel}
        onRemove={handleEditTemplateRemove}
        title={getText(
          `${intlPrefix}.form.sync_mode.te_out.location.edit_template_modal.title`
        )}
        getTokenOptions={getLocationTokenOptions}
      />
    </div>
  );
};

export default LocationPicker;
